/**
 * Nationality data for the checkout traveler forms.
 *
 * No backend API provides a nationality master list, so we keep a static set of
 * ISO 3166-1 alpha-2 codes and derive the German display names at runtime via
 * `Intl.DisplayNames`. This avoids maintaining ~250 hand-translated country names
 * and keeps the names in sync with the site's German-only market.
 *
 * Data contract with DaVinci (Participant.Passport):
 * - `NationalityCode` ← the stored ISO alpha-2 code (e.g. 'DE'), uppercase.
 * - `Nationality`     ← the German country name (e.g. 'Deutschland').
 *
 * The select stores the ISO code as its value; labels and the API `Nationality`
 * string both use the German name from `getNationalityName()`.
 */

import type { DropdownOption } from '@/components/ui/BaseDropdown.vue';

/**
 * Nationalities pinned to the top of the select (German market priority),
 * in this exact order. The rest follow alphabetically.
 *
 * These uppercase codes also match the three address countries ('de'/'at'/'ch'),
 * which is what the country → nationality auto-fill maps onto.
 */
export const PINNED_NATIONALITY_CODES = ['DE', 'AT', 'CH'] as const;

/**
 * Territories excluded from the nationality select. These ISO codes have no
 * permanent population, so they are not meaningful as a nationality. Add more
 * codes here (e.g. dependent territories) to trim the list further.
 */
export const EXCLUDED_NATIONALITY_CODES = new Set<string>([
  'AQ', // Antarctica
  'BV', // Bouvet Island
  'GS', // South Georgia & the South Sandwich Islands
  'HM', // Heard Island & McDonald Islands
  'IO', // British Indian Ocean Territory
  'TF', // French Southern Territories
  'UM', // United States Minor Outlying Islands
]);

/**
 * Full ISO 3166-1 alpha-2 code list. Names are resolved via Intl, so this only
 * needs the codes. The selectable list (NATIONALITY_CODES) is derived from this
 * by removing EXCLUDED_NATIONALITY_CODES.
 */
const ALL_ISO_3166_1_ALPHA2_CODES: readonly string[] = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

/**
 * Selectable nationality codes: the full ISO list minus excluded territories.
 */
export const NATIONALITY_CODES: readonly string[] = ALL_ISO_3166_1_ALPHA2_CODES.filter(
  (code) => !EXCLUDED_NATIONALITY_CODES.has(code)
);

/** Set of valid codes for O(1) validation lookups. */
export const NATIONALITY_CODE_SET = new Set<string>(NATIONALITY_CODES);

// Single shared instances — German market, so the locale is fixed.
const displayNames = new Intl.DisplayNames(['de'], { type: 'region' });
const collator = new Intl.Collator('de');

/**
 * Resolve the German country name for an ISO alpha-2 code (e.g. 'DE' → 'Deutschland').
 * Input is uppercased first because `Intl.DisplayNames` is case-sensitive and
 * silently returns the raw code (no throw) for lowercase input. Falls back to the
 * code itself if Intl cannot resolve it.
 */
export function getNationalityName(code: string): string {
  try {
    return displayNames.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Dropdown options for the nationality select: DE/AT/CH first (in that order),
 * the remaining countries sorted alphabetically by German name.
 *
 * Computed once at module load — the data is static and locale is fixed.
 */
export const NATIONALITY_OPTIONS: DropdownOption[] = (() => {
  const toOption = (code: string): DropdownOption => ({
    value: code,
    label: getNationalityName(code),
  });

  const pinned = PINNED_NATIONALITY_CODES.map(toOption);

  const rest = NATIONALITY_CODES.filter(
    (code) => !PINNED_NATIONALITY_CODES.includes(code as (typeof PINNED_NATIONALITY_CODES)[number])
  )
    .map(toOption)
    .sort((a, b) => collator.compare(a.label, b.label));

  return [...pinned, ...rest];
})();
